import { IVector2 } from '@inter-ikea-betrakta/viewer';
import { AssemblyInstructionMeasurementPosition } from '@inter-ikea-storageone/range';
import screenshotSettings from 'config/screenshotSettings';
import { useServiceSettings } from 'contexts/serviceSettings';
import { useTranslations } from 'hooks/useTranslations';
import { Fragment } from 'react';
import styled from 'styled-components';
import {
  adjustMeasurementsSystem,
  adjustMeasurementsSystemSummary
} from 'utils/adjustMeasurementsSystem';

const SvgText = styled.text`
  font-size: 1.5rem;
`;

type Props = {
  position: AssemblyInstructionMeasurementPosition;
  startPoint: IVector2;
  endPoint: IVector2;
  measurement: number;
};

const MeasurementLine = ({ startPoint, endPoint, measurement, position }: Props) => {
  const { useMetric } = useTranslations();
  const { translations } = useServiceSettings();
  const value = useMetric
    ? adjustMeasurementsSystemSummary(measurement)
    : adjustMeasurementsSystem(measurement, translations, useMetric);

  const BOTTOM_LINE_Y = screenshotSettings.default.size.y - 100;
  const TOP_LINE_Y = 100;
  const RIGHT_LINE_X = screenshotSettings.default.size.x - 150;
  const LEFT_LINE_X = 100;

  return position === AssemblyInstructionMeasurementPosition.BOTTOM ? (
    <Fragment>
      <line
        x1={startPoint.x}
        y1={BOTTOM_LINE_Y + 10}
        x2={startPoint.x}
        y2={BOTTOM_LINE_Y - 10}
        stroke="black"
        strokeWidth={3}
      />
      <line
        x1={startPoint.x}
        y1={BOTTOM_LINE_Y - 10}
        x2={startPoint.x}
        y2={startPoint.y}
        stroke="lightgrey"
        strokeWidth={1}
      />
      <line
        x1={startPoint.x}
        y1={BOTTOM_LINE_Y}
        x2={endPoint.x}
        y2={BOTTOM_LINE_Y}
        stroke="black"
        strokeWidth={3}
      />
      <line
        x1={endPoint.x}
        y1={BOTTOM_LINE_Y + 10}
        x2={endPoint.x}
        y2={BOTTOM_LINE_Y - 10}
        stroke="black"
        strokeWidth={3}
      />
      <line
        x1={endPoint.x}
        y1={BOTTOM_LINE_Y - 10}
        x2={endPoint.x}
        y2={endPoint.y}
        stroke="lightgrey"
        strokeWidth={1}
      />
      <SvgText
        x={(startPoint.x + endPoint.x) / 2}
        y={BOTTOM_LINE_Y + 40}
        fill="black"
        textAnchor="middle"
      >
        {value}
      </SvgText>
    </Fragment>
  ) : position === AssemblyInstructionMeasurementPosition.TOP ? (
    <Fragment>
      <line
        x1={startPoint.x}
        y1={TOP_LINE_Y - 10}
        x2={startPoint.x}
        y2={TOP_LINE_Y + 10}
        stroke="black"
        strokeWidth={3}
      />
      <line
        x1={startPoint.x}
        y1={TOP_LINE_Y + 10}
        x2={startPoint.x}
        y2={startPoint.y}
        stroke="lightgrey"
        strokeWidth={1}
      />
      <line
        x1={startPoint.x}
        y1={TOP_LINE_Y}
        x2={endPoint.x}
        y2={TOP_LINE_Y}
        stroke="black"
        strokeWidth={3}
      />
      <line
        x1={endPoint.x}
        y1={TOP_LINE_Y - 10}
        x2={endPoint.x}
        y2={TOP_LINE_Y + 10}
        stroke="black"
        strokeWidth={3}
      />
      <line
        x1={endPoint.x}
        y1={TOP_LINE_Y + 10}
        x2={endPoint.x}
        y2={endPoint.y}
        stroke="lightgrey"
        strokeWidth={1}
      />
      <SvgText
        x={(startPoint.x + endPoint.x) / 2}
        y={TOP_LINE_Y - 20}
        fill="black"
        textAnchor="middle"
      >
        {value}
      </SvgText>
    </Fragment>
  ) : position === AssemblyInstructionMeasurementPosition.RIGHT ? (
    <Fragment>
      <line
        x1={RIGHT_LINE_X - 10}
        y1={startPoint.y}
        x2={RIGHT_LINE_X + 10}
        y2={startPoint.y}
        stroke="black"
        strokeWidth={3}
      />
      <line
        x1={RIGHT_LINE_X - 10}
        y1={startPoint.y}
        x2={startPoint.x}
        y2={startPoint.y}
        stroke="lightgrey"
        strokeWidth={1}
      />
      <line
        x1={RIGHT_LINE_X}
        y1={startPoint.y}
        x2={RIGHT_LINE_X}
        y2={endPoint.y}
        stroke="black"
        strokeWidth={3}
      />
      <line
        x1={RIGHT_LINE_X - 10}
        y1={endPoint.y}
        x2={RIGHT_LINE_X + 10}
        y2={endPoint.y}
        stroke="black"
        strokeWidth={3}
      />
      <line
        x1={RIGHT_LINE_X - 10}
        y1={endPoint.y}
        x2={endPoint.x}
        y2={endPoint.y}
        stroke="lightgrey"
        strokeWidth={1}
      />
      <SvgText
        x={RIGHT_LINE_X + 40}
        y={(startPoint.y + endPoint.y) / 2 + 10}
        fill="black"
        textAnchor="middle"
      >
        {value}
      </SvgText>
    </Fragment>
  ) : position === AssemblyInstructionMeasurementPosition.LEFT ? (
    <Fragment>
      <line
        x1={LEFT_LINE_X - 10}
        y1={startPoint.y}
        x2={LEFT_LINE_X + 10}
        y2={startPoint.y}
        stroke="black"
        strokeWidth={3}
      />
      <line
        x1={LEFT_LINE_X + 10}
        y1={startPoint.y}
        x2={startPoint.x}
        y2={startPoint.y}
        stroke="lightgrey"
        strokeWidth={1}
      />
      <line
        x1={LEFT_LINE_X}
        y1={startPoint.y}
        x2={LEFT_LINE_X}
        y2={endPoint.y}
        stroke="black"
        strokeWidth={3}
      />
      <line
        x1={LEFT_LINE_X - 10}
        y1={endPoint.y}
        x2={LEFT_LINE_X + 10}
        y2={endPoint.y}
        stroke="black"
        strokeWidth={3}
      />
      <line
        x1={LEFT_LINE_X + 10}
        y1={endPoint.y}
        x2={endPoint.x}
        y2={endPoint.y}
        stroke="lightgrey"
        strokeWidth={1}
      />
      <SvgText
        x={LEFT_LINE_X - 40}
        y={(startPoint.y + endPoint.y) / 2 + 10}
        fill="black"
        textAnchor="middle"
      >
        {value}
      </SvgText>
    </Fragment>
  ) : null;
};
export default MeasurementLine;
